import React, { Component } from 'react'
import Loader from '../../components/loader'
import { Field, reduxForm } from 'redux-form'
import { History } from 'history'
import { InputField, SelectField, TextareaField } from '../../components/inputs'
import { ReduxFormProps } from '../../forms'
import { StipulationMapping } from '../tasks/types'

export interface Input {
  id: string
  adminVerificationType?: string
  mode?: string
  title?: string
  description?: string
}

export interface Meta {
  touched: boolean
  error: string
}

export interface Props {
  mapping: StipulationMapping
  history?: History
}

export interface FormProps {
  adminVerificationType?: string
  mode?: string
  title?: string
  description?: string
}

export class StipulationMappingForm extends Component<
  ReduxFormProps<Props, FormProps>
> {
  static displayName = 'StipulationMappingForm'

  static verificationTypes = [
    'APP_FOR_TITLE',
    'CARFAX_FRAUD_REVIEW',
    'CONTACT_LIENHOLDER',
    'CURRENT_MORTGAGE_STATEMENT',
    'DRIVER_LICENSE',
    'FINAL_LOAN',
    'IMAGE',
    'LOSS_PAYEE',
    'PROOF_OF_EMPLOYMENT',
    'PROOF_OF_INSURANCE',
    'PROOF_OF_RESIDENCE',
    'REGISTRATION',
    'REVIEW_FOR_FRAUD',
    'REVIEW_OFAC',
    'REVIEW_PLAID_DATA_MISMATCH',
    'REVIEW_STATEMENT',
    'REVIEW_USER_DATA_MISMATCH',
    'SIGN_LOAN_AGREEMENT',
    'SSN_CARD',
    'SUBMIT_PROOF_OF_INCOME',
    'TEXT',
    'TITLE',
    'UPDATE_LIENHOLDER_INFO',
    'VERIFY_FUNDS',
    'VERIFY_MILEAGE',
  ]

  static modes = ['IMAGE', 'TEXT']

  makeOptions = (values, defaultValue) => {
    const opts = values.map((v) => <option key={v}>{v}</option>)

    return [
      <option value="" key={0}>
        {defaultValue}
      </option>,
      ...opts,
    ]
  }

  verificationOptions = () =>
    this.makeOptions(StipulationMappingForm.verificationTypes, 'Not mapped')

  modeOptions = () =>
    this.makeOptions(StipulationMappingForm.modes, 'Not configured')

  render() {
    const { handleSubmit, pristine, submitting } = this.props

    const avOpts = this.verificationOptions()
    const modeOpts = this.modeOptions()

    return (
      <form onSubmit={handleSubmit} className="form">
        <h4>Map to existing verification:</h4>
        <Field
          label="Admin Verification Type"
          name="adminVerificationType"
          component={SelectField}
        >
          {avOpts}
        </Field>
        <div className="separator">or</div>
        <h4>Configure as new dynamic stipulation:</h4>
        <Field label="Mode" name="mode" component={SelectField}>
          {modeOpts}
        </Field>
        <Field label="Title" name="title" component={InputField} />
        <Field
          label="Description"
          name="description"
          component={TextareaField}
        />
        <button
          className="button button--primary"
          type="submit"
          disabled={pristine || submitting}
        >
          Save
          <Loader isLoading={submitting} className="loader--button" />
        </button>
      </form>
    )
  }
}

export const validateFields = (values: Input) => {
  const errors: Input = { id: values.id }
  if (!values.adminVerificationType && !values.mode) {
    errors.adminVerificationType = 'Either AV Type or Mode must be specified'
  }

  if (values.adminVerificationType && values.mode) {
    errors.adminVerificationType =
      'Either AV Type or Mode must be specified, but not both'
  }

  if (values.mode && !values.title) {
    errors.title = 'Title must be specified for custom stipulations'
  }

  if (values.mode && !values.description) {
    errors.description = 'Description must be specified for custom stipulations'
  }

  return errors
}

export default reduxForm<FormProps, Props>({
  form: 'stipulationMappingForm',
  validate: validateFields,
  enableReinitialize: true,
})(StipulationMappingForm)
